import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Camera, Upload, Loader2, CheckCircle2 } from "lucide-react";

/**
 * Bouton "Scanner un document" avec OCR + extraction IA.
 *
 * Props:
 *  - onExtract(data): callback appelé avec le JSON extrait
 *  - label?: texte du bouton (défaut: "Scanner la carte grise")
 *  - testId?: data-testid (défaut: "scan-doc-btn")
 */
export default function ScanDocumentBox({ onExtract, label, testId }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pickFile = () => fileRef.current?.click();
  const takePhoto = () => cameraRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Format non supporté (utilisez JPEG, PNG ou WEBP)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo)");
      return;
    }
    setLoading(true);
    setDone(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ocr/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data?.ok) {
        toast.error(data?.error || "L'IA n'a pas pu lire le document");
        return;
      }
      const extracted = data.data || {};
      // Count non-empty fields
      const n = Object.values(extracted).filter((v) => v !== null && v !== "" && v !== undefined).length;
      onExtract?.(extracted);
      setDone(true);
      toast.success(`${n} champ${n > 1 ? "s" : ""} pré-rempli${n > 1 ? "s" : ""} par l'IA`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur de lecture du document");
    }
    setLoading(false);
  };

  return (
    <div
      className="border border-dashed border-klein/40 bg-klein/5 rounded-sm p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
      data-testid="scan-doc-box"
    >
      <div className="flex items-center gap-2 text-sm flex-1">
        <Sparkles size={16} className="text-klein shrink-0" />
        <div className="leading-tight">
          <div className="font-semibold">{label || "Scanner la carte grise"}</div>
          <div className="text-[11px] text-muted-foreground">
            L'IA lit le document et pré-remplit la marque, le modèle, la plaque, le VIN, etc.
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={takePhoto}
          data-testid={(testId || "scan-doc-btn") + "-camera"}
          className="rounded-sm sm:hidden"
        >
          {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Camera size={14} className="mr-1" />}
          Photo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={pickFile}
          data-testid={testId || "scan-doc-btn"}
          className="rounded-sm"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin mr-1" /> Analyse en cours…
            </>
          ) : done ? (
            <>
              <CheckCircle2 size={14} className="mr-1 text-emerald-600" /> Re-scanner
            </>
          ) : (
            <>
              <Upload size={14} className="mr-1" /> Importer
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
